/**
 * Tipos de la API GET /api/v1/profitability/*
 */

export type ProfitabilityDateColumn = 'processed_at' | 'order_created_at' | 'created_at' | 'updated_at';

/** El módulo de rentabilidad filtra siempre por fecha de creación del pedido (API `order_created_at`). */
export const PROFITABILITY_DATE_COLUMN: Extract<ProfitabilityDateColumn, 'order_created_at'> = 'order_created_at';

export interface ProfitabilityCommonFilters {
  start_date?: string;
  end_date?: string;
  date_column?: ProfitabilityDateColumn;
  shop_id?: number;
}

/** Conteos por `orders.status_id` en el mismo periodo/alcance (todos los estados). */
export interface ProfitabilityOrderCounts {
  total: number;
  /** Estado 7 */
  shipped: number;
  /** Estados 1–6 */
  not_yet_shipped: number;
  /** Estado 6 */
  prepared: number;
  /** Estado 8 */
  returned: number;
  /** Estado 9 */
  cancelled: number;
  /** Claves `status_id` como string → cantidad */
  by_status_id?: Record<string, number>;
}

/** Conteos logísticos (envío / entrega / devolución por estado de envío). */
export interface ProfitabilityLogisticCounts {
  delivered: number;
  returned: number;
  without_mapping: number;
  by_shipment_status_id?: Record<string, number>;
}

export interface ProfitabilitySummaryResponse {
  filters: Record<string, unknown>;
  /** Pedidos incluidos en cálculo de rentabilidad (solo estados 6, 7 u 8). */
  orders_count: number;
  /** Opcional: conteos por estado en el periodo (API reciente). */
  order_counts?: ProfitabilityOrderCounts;
  /** Conteos por logística (entregas / devueltos según estado de envío). */
  logistic_counts?: ProfitabilityLogisticCounts;
  totals: {
    recognized_revenue: number;
    recognized_cogs: number;
    recognized_logistics: number;
    /** Impuestos reconocidos (opcional hasta que la API los exponga). */
    recognized_taxes?: number;
    /** Beneficio atribuible solo a pedidos (antes era el único `profit`). */
    operating_profit: number;
    /** Gasto publicitario del periodo en el alcance. */
    ad_spend: number;
    /** Beneficio neto: operating_profit − ad_spend. */
    profit: number;
  };
  averages: {
    /** Sobre beneficio neto (`totals.profit`). */
    profit_per_order: number | null;
    /** Margen % sobre ingresos; basado en beneficio neto. */
    margin_percent: number | null;
  };
}

export interface ProfitabilitySummaryTotals {
  recognized_revenue: number;
  recognized_cogs: number;
  recognized_logistics: number;
  recognized_taxes?: number;
  operating_profit: number;
  ad_spend: number;
  profit: number;
}

export interface ProfitabilitySummaryAverages {
  profit_per_order: number | null;
  margin_percent: number | null;
}

/** GET /profitability/summary/daily */
export interface ProfitabilitySummaryDay {
  date: string;
  orders_count: number;
  order_counts?: ProfitabilityOrderCounts;
  logistic_counts?: ProfitabilityLogisticCounts;
  totals: ProfitabilitySummaryTotals;
  averages: ProfitabilitySummaryAverages;
}

export interface ProfitabilitySummaryDailyQuery extends ProfitabilityCommonFilters {
  /** PREPAID (1) incluye también pedidos con payment_method_id NULL. */
  payment_method_id?: number;
  /** 0 = sin mapeo logístico. */
  shipment_status_id?: number;
}

export interface ProfitabilitySummaryBreakdownQuery extends ProfitabilitySummaryDailyQuery {
  page?: number;
  per_page?: number;
}

export interface ProfitabilitySummaryDailyResponse {
  filters: Record<string, unknown>;
  days: ProfitabilitySummaryDay[];
  orders_count: number;
  order_counts?: ProfitabilityOrderCounts;
  logistic_counts?: ProfitabilityLogisticCounts;
  totals: ProfitabilitySummaryTotals;
  averages: ProfitabilitySummaryAverages;
}

/** GET /profitability/summary/cogs */
export interface ProfitabilitySummaryCogsProduct {
  product_id: number;
  name: string;
  code?: string | null;
  image_url?: string | null;
  orders_count: number;
  quantity: number;
  average_unit_cost: number;
  total_cogs: number;
}

export interface ProfitabilitySummaryCogsTotals {
  orders_count: number;
  quantity: number;
  average_unit_cost: number;
  total_cogs: number;
}

export interface ProfitabilitySummaryCogsResponse {
  filters: Record<string, unknown>;
  data: ProfitabilitySummaryCogsProduct[];
  meta: ProfitabilityOrdersPaginationMeta;
  totals: ProfitabilitySummaryCogsTotals;
}

/** GET /profitability/summary/shipping-costs */
export interface ProfitabilitySummaryShippingCostCourier {
  id: number;
  name: string;
}

export interface ProfitabilitySummaryShippingCostService {
  id: number;
  code: string;
  description: string;
}

export interface ProfitabilitySummaryShippingCostBySource {
  accounting?: number;
  default?: number;
  estimated?: number;
}

export interface ProfitabilitySummaryShippingCostRow {
  courier: ProfitabilitySummaryShippingCostCourier | null;
  courier_service: ProfitabilitySummaryShippingCostService | null;
  orders_count: number;
  average_cost: number;
  total: number;
  by_source: ProfitabilitySummaryShippingCostBySource;
}

export interface ProfitabilitySummaryShippingCostsTotals {
  orders_count: number;
  average_cost: number;
  total: number;
  by_source: ProfitabilitySummaryShippingCostBySource;
}

export interface ProfitabilitySummaryShippingCostsResponse {
  filters?: Record<string, unknown>;
  data: ProfitabilitySummaryShippingCostRow[];
  meta: ProfitabilityOrdersPaginationMeta;
  totals: ProfitabilitySummaryShippingCostsTotals;
}

export interface ProfitabilityRecognizedAmounts {
  recognized_revenue: number;
  recognized_cogs: number;
  recognized_logistics: number;
  profit: number;
}

export interface ProfitabilityOrderLine {
  order_line_id: number;
  product_id: number | null;
  pack_id: number | null;
  line_cogs: number;
  allocated_revenue: number;
  allocated_logistics: number;
  line_profit: number;
}

export interface ProfitabilityOrder {
  order_id: number;
  ref: string;
  status_id: number;
  payment_method_id: number | null;
  shop_id: number | null;
  total: number;
  shipment_status_id: number | null;
  recognized: ProfitabilityRecognizedAmounts;
  order_profit: number;
  logistics_source: string | null;
  lines: ProfitabilityOrderLine[];
  /** Solo en detalle GET /orders/{id} */
  sum_line_cogs?: number;
}

export interface ProfitabilityOrdersPaginationMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface ProfitabilityOrdersListResponse {
  data: ProfitabilityOrder[];
  meta: ProfitabilityOrdersPaginationMeta;
}

export interface ProfitabilityOrderDetailResponse {
  data: ProfitabilityOrder;
}

/** Estadísticas de cumplimiento / logística por producto (API `by-product`). */
export interface ProfitabilityByProductFulfillment {
  orders: number;
  order_status_prepared: number;
  order_status_shipped: number;
  order_status_returned: number;
  logistic_delivered: number;
  logistic_returned: number;
  logistic_without_mapping: number;
}

export interface ProfitabilityByProductRow {
  product_id: number;
  product_name: string;
  /** SKU / referencia (opcional en API). */
  code?: string | null;
  /** URL de imagen en CDN (opcional). */
  image_url?: string | null;
  order_lines_count: number;
  allocated_revenue: number;
  line_cogs: number;
  allocated_logistics: number;
  /** Suma del beneficio por líneas de pedido (valor antiguo de `profit`). */
  operating_profit: number;
  /** Gasto publicitario atribuido a este producto. */
  ad_spend: number;
  /** Beneficio neto: operating_profit − ad_spend. Puede ser negativo si solo hay publicidad. */
  profit: number;
  fulfillment?: ProfitabilityByProductFulfillment;
}

export interface ProfitabilityByProductResponse {
  data: ProfitabilityByProductRow[];
}

export interface ProfitabilityOrdersQuery extends ProfitabilityCommonFilters {
  page?: number;
  per_page?: number;
}

/** GET /profitability/summary/orders — pedidos del resumen con mismos filtros. */
export interface ProfitabilitySummaryOrderTotals {
  recognized_revenue: number;
  recognized_cogs: number;
  recognized_logistics: number;
  operating_profit: number;
}

export interface ProfitabilitySummaryOrder {
  order_id: number;
  ref: string;
  shop_id: number;
  shop_name: string;
  status_id: number;
  status_name?: string | null;
  payment_method_id: number;
  payment_method_name?: string | null;
  shipment_status_id: number;
  shipment_status_name?: string | null;
  total: number;
  reference_date: string;
  totals: ProfitabilitySummaryOrderTotals;
  margin_percent: number;
  logistics_source: string | null;
}

export interface ProfitabilitySummaryOrdersQuery extends ProfitabilityCommonFilters {
  page?: number;
  per_page?: number;
  /** PREPAID (1) incluye también pedidos con payment_method_id NULL. */
  payment_method_id?: number;
  /** 0 = sin mapeo logístico. */
  shipment_status_id?: number;
}

export interface ProfitabilitySummaryOrdersResponse {
  filters: Record<string, unknown>;
  data: ProfitabilitySummaryOrder[];
  meta: ProfitabilityOrdersPaginationMeta;
}

/** GET/POST/PUT /profitability/default-shipping-costs */
export interface ProfitabilityDefaultShippingCost {
  id: number;
  team_id: number;
  courier_service_id: number;
  cost: number;
  /** Cargo COD fijo (misma moneda que el coste de envío). */
  cod_fee_fixed: number;
  /** Cargo COD en porcentaje (p. ej. 2.5 = 2,5 %). */
  cod_fee_percent: number;
  created_at: string;
  updated_at: string;
}

export interface ProfitabilityDefaultShippingCostsListResponse {
  data: ProfitabilityDefaultShippingCost[];
}

export interface ProfitabilityDefaultShippingCostItemResponse {
  data: ProfitabilityDefaultShippingCost;
}

export interface ProfitabilityDefaultShippingCostPayload {
  team_id: number;
  courier_service_id: number;
  cost: number;
  /** Opcional; por defecto en API: 0 */
  cod_fee_fixed?: number;
  /** Opcional; por defecto en API: 0 */
  cod_fee_percent?: number;
}

/** GET /profitability/default-shipping-costs/missing */
export interface ProfitabilityMissingDefaultShippingCostCourier {
  id: number;
  name: string;
}

export interface ProfitabilityMissingDefaultShippingCost {
  team_id: number;
  team_name: string;
  courier?: ProfitabilityMissingDefaultShippingCostCourier | null;
  courier_service_id: number;
  courier_service_code: string;
  courier_service_description: string;
  orders_count: number;
}

export interface ProfitabilityMissingDefaultShippingCostsQuery {
  team_id?: number;
  page?: number;
  per_page?: number;
}

export interface ProfitabilityMissingDefaultShippingCostsResponse {
  data: ProfitabilityMissingDefaultShippingCost[];
  meta: ProfitabilityOrdersPaginationMeta;
}

/** GET /profitability/ad-spend/campaigns */
export interface ProfitabilityAdCampaign {
  id: number;
  team_id: number;
  external_id: string;
  name: string;
  product_id: number | null;
  pack_id: number | null;
  /** Suma acumulada del gasto importado para la campaña */
  total_spend?: number | null;
  /** Fecha (YYYY-MM-DD) del último día con gasto registrado */
  last_spend_on?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ProfitabilityAdCampaignsListResponse {
  data: ProfitabilityAdCampaign[];
}

export interface ProfitabilityAdCampaignItemResponse {
  data: ProfitabilityAdCampaign;
}

/** PATCH /profitability/ad-spend/campaigns/{id} — no asignar product_id y pack_id a la vez */
export interface ProfitabilityAdCampaignPatchPayload {
  product_id?: number | null;
  pack_id?: number | null;
}

/** POST /profitability/ad-spend/import */
export interface ProfitabilityAdSpendImportSummary {
  rows_read: number;
  rows_skipped: number;
  campaigns_created: number;
  spends_created: number;
  spends_updated: number;
  spends_unchanged: number;
}

export interface ProfitabilityAdSpendImportCampaignCreated {
  id: number;
  external_id: string;
  name: string;
}

export interface ProfitabilityAdSpendImportSpendRow {
  ad_campaign_id: number;
  external_id: string;
  date: string;
  amount: number;
}

export interface ProfitabilityAdSpendImportChanges {
  campaigns_created: ProfitabilityAdSpendImportCampaignCreated[];
  spends_created: ProfitabilityAdSpendImportSpendRow[];
  spends_updated: ProfitabilityAdSpendImportSpendRow[];
  errors: unknown[];
}

export interface ProfitabilityAdSpendImportResponse {
  summary: ProfitabilityAdSpendImportSummary;
  changes: ProfitabilityAdSpendImportChanges;
  /** Si el backend recorta el detalle en la respuesta */
  truncated?: boolean;
  message?: string;
}

/** GET/POST/PUT /profitability/product-costs */
export interface ProfitabilityProductCost {
  id: number;
  product_id: number;
  /** Presente en listado GET */
  product_name?: string;
  product_code?: string | null;
  cost: number;
  /** null = coste base del producto */
  effective_from: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProfitabilityProductCostsListResponse {
  data: ProfitabilityProductCost[];
}

export interface ProfitabilityProductCostItemResponse {
  data: ProfitabilityProductCost;
}

export interface ProfitabilityProductCostPayload {
  product_id: number;
  cost: number;
  /** Omitir en PUT para conservar; null = coste base */
  effective_from?: string | null;
}

export interface ProfitabilityProductCostsQuery {
  product_id?: number;
}

/** GET /profitability/products-without-cost */
export interface ProfitabilityProductWithoutCost {
  id: number;
  name: string;
  code?: string | null;
  image_url?: string | null;
  team_id: number;
  orders_count: number;
}

export interface ProfitabilityProductsWithoutCostQuery {
  search?: string;
  page?: number;
  per_page?: number;
}

export interface ProfitabilityProductsWithoutCostResponse {
  data: ProfitabilityProductWithoutCost[];
  meta: ProfitabilityOrdersPaginationMeta;
}
